export interface ReplyAllContact {
	name?: string | null
	email: string
	mailboxHash?: string | null
}

export interface ReplyAllEmail<T extends ReplyAllContact = ReplyAllContact> {
	type: "INBOUND" | "OUTBOUND"
	from: T | null
	to: T[]
	cc: T[]
	bcc: T[]
	originalRecipient?: string | null
}

const normalize = (email: string | null | undefined) =>
	email?.trim().toLowerCase() ?? ""

const hasRecipients = (email: ReplyAllEmail) =>
	email.to.length > 0 || email.cc.length > 0 || email.bcc.length > 0

/**
 * Newest email that still carries a recipient list, inbound preferred. Portal
 * and widget replies persist empty to/cc, and a reply that lost its Cc would
 * otherwise erase the distribution list for every later reply.
 */
export function pickReplyAllSource<T extends ReplyAllContact>(
	emailsNewestFirst: ReplyAllEmail<T>[],
): ReplyAllEmail<T> | null {
	return (
		emailsNewestFirst.find((e) => e.type === "INBOUND" && hasRecipients(e)) ??
		emailsNewestFirst.find(hasRecipients) ??
		null
	)
}

/**
 * Cc/Bcc for a reply-all. The inbound sender is a candidate too, so a forwarder
 * whose thread got attributed to the original sender still receives the answer.
 * Workspace-owned addresses and the contact themselves are never included: a
 * customer forwarding from their own support inbox puts our address in the
 * original To/Cc, and replying to it loops back into us.
 */
export function computeReplyAllRecipients<T extends ReplyAllContact>({
	emailsNewestFirst,
	selfEmails,
	contactEmail,
}: {
	emailsNewestFirst: ReplyAllEmail<T>[]
	selfEmails: (string | null | undefined)[]
	contactEmail: string | null | undefined
}): { cc: T[]; bcc: T[] } {
	const source = pickReplyAllSource(emailsNewestFirst)
	if (!source) return { cc: [], bcc: [] }

	const selfSet = new Set(
		[
			...selfEmails,
			source.type === "INBOUND" ? source.originalRecipient : null,
			...emailsNewestFirst
				.filter((e) => e.type === "OUTBOUND")
				.map((e) => e.from?.email),
		]
			.map(normalize)
			.filter(Boolean),
	)
	const contactNormalized = normalize(contactEmail)
	const isExcluded = (email: string) => {
		const normalized = normalize(email)
		if (!normalized) return true
		return selfSet.has(normalized) || normalized === contactNormalized
	}

	const dedupe = (contacts: T[]) => {
		const seen = new Map<string, T>()
		for (const c of contacts) {
			if (isExcluded(c.email)) continue
			const key = normalize(c.email)
			if (!seen.has(key)) seen.set(key, c)
		}
		return Array.from(seen.values())
	}

	const inboundSender =
		source.type === "INBOUND" && source.from ? [source.from] : []

	return {
		cc: dedupe([...inboundSender, ...source.cc, ...source.to]),
		bcc: dedupe(source.bcc),
	}
}
